/**
 * Finish Effect Definitions
 *
 * Cosmetic effects that play on your player row in tournament round-results
 * the moment your round is final. Visible to every player watching — the
 * social-flex angle that frames can't deliver.
 *
 * Each effect is a CSS class identifier; styling lives in finishEffects.css.
 */

import type { Rarity, Acquisition } from './rarity';

export interface FinishEffect {
  id: string;
  name: string;
  description: string;
  /** Buyable price; for earned effects this is the box dust/refund value. */
  price: number;
  rarity: Rarity;
  /** Omit for buyable ('shop'); 'earned' = challenge/box only. */
  acquisition?: Acquisition;
  /** CSS class on the overlay wrapper. Empty string for the no-effect default. */
  effectClass: string;
  /** Swatch color used in store previews. */
  swatchColor: string;
  /** True for the free default (no effect). */
  isDefault?: boolean;
}

export const DEFAULT_FINISH_EFFECT_ID = 'none';

// All finish effects (except the free default) are earned-only — they are the
// tournament-visible "show-off" reward, awarded by challenges / box drops.
export const FINISH_EFFECTS: FinishEffect[] = [
  {
    id: 'none',
    name: 'No Effect',
    description: 'The default — your row just sits there. Free.',
    price: 0,
    rarity: 'common',
    effectClass: '',
    swatchColor: '#64748b',
    isDefault: true,
  },
  {
    id: 'confetti',
    name: 'Confetti Burst',
    description: 'A 1.5-second shower of colored confetti rains across your row.',
    price: 350,
    rarity: 'rare',
    acquisition: 'earned',
    effectClass: 'finish-effect-confetti',
    swatchColor: '#fbbf24',
  },
  {
    id: 'flame-burst',
    name: 'Flame Burst',
    description: 'Orange flames erupt from your row and lick upward as you finish.',
    price: 400,
    rarity: 'epic',
    acquisition: 'earned',
    effectClass: 'finish-effect-flame',
    swatchColor: '#f97316',
  },
  {
    id: 'lightning-strike',
    name: 'Lightning Strike',
    description: 'A sharp electric flash and jagged bolt mark your finish.',
    price: 500,
    rarity: 'legendary',
    acquisition: 'earned',
    effectClass: 'finish-effect-lightning',
    swatchColor: '#60a5fa',
  },
];

export function getFinishEffect(id: string | undefined): FinishEffect {
  return FINISH_EFFECTS.find(e => e.id === id) ?? FINISH_EFFECTS[0];
}
